import { z } from "zod";

/** Prefixo de todas as rotas da API. Em dev o Vite encaminha para 127.0.0.1:8000. */
export const BASE_DA_API = "/api";

/** Erro de qualquer chamada à API, com o status HTTP quando houve resposta. */
export class ErroDaApi extends Error {
  readonly status: number | null;
  /** O `codigo` do envelope de erro da API, quando ela respondeu no formato acordado. */
  readonly codigo: string | null;
  /* O `detalhe` do envelope, sem interpretação nenhuma aqui.

     Ele carrega a lista de impedimentos do envio e os problemas por pergunta do rascunho — que
     é o que permite pôr a recusa do servidor NO CAMPO certo, e não numa faixa vermelha genérica
     no topo. Sai como `unknown` de propósito: quem sabe a forma de cada detalhe é o módulo do
     domínio, que a valida com zod; esta camada não pode virar um catálogo de formatos. */
  readonly detalhe: unknown;

  constructor(
    mensagem: string,
    status: number | null = null,
    codigo: string | null = null,
    detalhe: unknown = undefined,
  ) {
    super(mensagem);
    this.name = "ErroDaApi";
    this.status = status;
    this.codigo = codigo;
    this.detalhe = detalhe;
  }
}

type Metodo = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface OpcoesDaChamada {
  signal?: AbortSignal;
  /** Corpo JSON. Ausente manda a requisição sem corpo e sem `Content-Type`. */
  corpo?: unknown;
  /* Corpo multipart. Vai sem `Content-Type` de propósito: o cabeçalho de um multipart carrega
     o `boundary`, que só o navegador conhece — declará-lo à mão produz um corpo que o servidor
     não consegue separar em campos. */
  formulario?: FormData;
}

async function chamar(
  caminho: string,
  metodo: Metodo,
  opcoes: OpcoesDaChamada = {},
): Promise<Response> {
  const temCorpo = opcoes.corpo !== undefined;
  const corpo = opcoes.formulario ?? (temCorpo ? JSON.stringify(opcoes.corpo) : undefined);

  try {
    return await fetch(`${BASE_DA_API}${caminho}`, {
      method: metodo,
      headers: {
        Accept: "application/json",
        ...(temCorpo ? { "Content-Type": "application/json" } : {}),
      },
      // O cookie de sessão é HttpOnly: este código nunca o lê, só faz o navegador enviá-lo.
      credentials: "same-origin",
      ...(corpo !== undefined ? { body: corpo } : {}),
      ...(opcoes.signal ? { signal: opcoes.signal } : {}),
    });
  } catch (causa) {
    if (causa instanceof DOMException && causa.name === "AbortError") throw causa;
    throw new ErroDaApi("Não foi possível falar com a API.");
  }
}

async function lerJson<E extends z.ZodType>(
  resposta: Response,
  esquema: E,
): Promise<z.output<E>> {
  let bruto: unknown;
  try {
    bruto = await resposta.json();
  } catch {
    throw new ErroDaApi("A API respondeu algo que não é JSON.", resposta.status);
  }

  const analise = esquema.safeParse(bruto);
  if (!analise.success) {
    throw new ErroDaApi("A resposta da API não bate com o contrato.", resposta.status);
  }
  return analise.data;
}

function exigirSucesso(resposta: Response): void {
  if (!resposta.ok) {
    throw new ErroDaApi(`A API respondeu ${resposta.status}.`, resposta.status);
  }
}

/**
 * `GET` que devolve JSON, com a mensagem da recusa preservada.
 *
 * A recusa de uma leitura também é escrita para quem está do outro lado da tela — "esta trilha
 * de kickoff não faz parte da sua implantação" diz o que aconteceu e o que fazer; "a API
 * respondeu 404" obriga a pessoa a adivinhar. Quando o corpo não vem no envelope acordado, cai
 * na frase genérica, que é o mesmo comportamento de antes.
 */
export async function pedirJson<E extends z.ZodType>(
  caminho: string,
  esquema: E,
  opcoes: OpcoesDaChamada = {},
): Promise<z.output<E>> {
  const resposta = await chamar(caminho, "GET", opcoes);
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return lerJson(resposta, esquema);
}

/**
 * `POST` que devolve JSON, com a mensagem do erro preservada.
 *
 * A API responde `{"erro":{"codigo","mensagem"}}` e a mensagem é escrita para quem está do
 * outro lado da tela — "este Recurso Plus pertence a um módulo que não foi contratado" ajuda,
 * "a API respondeu 422" não. Quando o corpo do erro não vem no formato esperado, cai na frase
 * genérica em vez de mostrar json cru.
 */
export async function enviarJson<E extends z.ZodType>(
  caminho: string,
  esquema: E,
  corpo: unknown,
  opcoes: Pick<OpcoesDaChamada, "signal"> = {},
): Promise<z.output<E>> {
  const resposta = await chamar(caminho, "POST", { ...opcoes, corpo });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return lerJson(resposta, esquema);
}

/**
 * `PUT` que devolve JSON. Mesmo tratamento de erro do `POST`.
 *
 * Existe porque o salvamento de rascunho do kickoff é idempotente por contrato — reenviar o
 * mesmo corpo deixa o mesmo estado — e o método precisa dizer isso. Um `POST` faria a tela
 * prometer criação a cada tecla salva.
 */
export async function atualizarJson<E extends z.ZodType>(
  caminho: string,
  esquema: E,
  corpo: unknown,
): Promise<z.output<E>> {
  const resposta = await chamar(caminho, "PUT", { corpo });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return lerJson(resposta, esquema);
}

/**
 * `PATCH` que devolve JSON. Mesmo tratamento de erro do `POST`.
 *
 * Existe porque a correção de um item da ata é **parcial** por contrato: `PedidoDeEdicaoDeItem`
 * distingue "campo ausente" de "`null` enviado", e um `PUT` obrigaria a tela a reenviar
 * `visivel_ao_cliente` a cada correção de vírgula — um campo esquecido nesse reenvio voltaria ao
 * padrão `false`, ou, pior, seria reenviado errado. O método precisa dizer isso.
 */
export async function editarJson<E extends z.ZodType>(
  caminho: string,
  esquema: E,
  corpo: unknown,
): Promise<z.output<E>> {
  const resposta = await chamar(caminho, "PATCH", { corpo });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return lerJson(resposta, esquema);
}

/** `POST` (ou `PUT`) de `multipart/form-data` — o envio de anexo, que carrega arquivo e não JSON. */
export async function enviarFormulario<E extends z.ZodType>(
  caminho: string,
  esquema: E,
  formulario: FormData,
  metodo: "POST" | "PUT" = "POST",
): Promise<z.output<E>> {
  const resposta = await chamar(caminho, metodo, { formulario });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return lerJson(resposta, esquema);
}

/**
 * `DELETE` cujo sucesso é `204`, preservando a mensagem da recusa.
 *
 * Diferente de `pedirSemConteudo`: aqui a recusa tem texto escrito para quem está do outro lado
 * ("esta trilha já foi enviada e está fechada para alteração"), e trocá-la por "a API respondeu
 * 409" transformaria uma explicação em código de status.
 */
export async function apagar(caminho: string): Promise<void> {
  const resposta = await chamar(caminho, "DELETE");
  if (!resposta.ok) throw await erroDaResposta(resposta);
}

/** `POST` de comando cujo sucesso é `204`, sem corpo de resposta. */
export function enviarSemConteudo(caminho: string): Promise<void> {
  return pedirSemConteudo(caminho, "POST");
}

/** `POST` com corpo JSON cujo sucesso é `204` — a resposta de uma pesquisa, por exemplo. */
export function enviarJsonSemConteudo(caminho: string, corpo: unknown): Promise<void> {
  return pedirSemConteudo(caminho, "POST", { corpo });
}

/**
 * `DELETE` que **devolve corpo** — o ato cujo efeito precisa ser lido de volta.
 *
 * O primeiro caso é tirar uma pergunta do catálogo do kickoff: o verbo é `DELETE` porque é o ato
 * de quem edita, mas nenhuma linha é apagada — publica-se uma versão nova sem ela, e a resposta
 * traz o número dessa versão. Um `204` obrigaria a tela a recarregar para descobrir o que
 * acabou de acontecer, e a frase que ela mostra depende justamente desse número.
 */
export async function apagarJson<E extends z.ZodType>(
  caminho: string,
  esquema: E,
): Promise<z.output<E>> {
  const resposta = await chamar(caminho, "DELETE");
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return lerJson(resposta, esquema);
}

const esquemaDoErro = z.object({
  erro: z.object({
    codigo: z.string(),
    mensagem: z.string().min(1),
    /* Opcional porque `envelope()` só inclui a chave quando há detalhe. `unknown` porque a forma
       varia por rota — quem sabe lê-la é o módulo do domínio. */
    detalhe: z.unknown().optional(),
  }),
});

async function erroDaResposta(resposta: Response): Promise<ErroDaApi> {
  try {
    const analise = esquemaDoErro.safeParse(await resposta.json());
    if (analise.success) {
      const erro = analise.data.erro;
      return new ErroDaApi(erro.mensagem, resposta.status, erro.codigo, erro.detalhe);
    }
  } catch {
    /* corpo vazio ou não-JSON: cai na mensagem genérica abaixo */
  }
  return new ErroDaApi(`A API respondeu ${resposta.status}.`, resposta.status);
}

/** Chamada cujo sucesso é `204 No Content`: não há corpo para ler nem validar. */
async function pedirSemConteudo(
  caminho: string,
  metodo: Metodo,
  opcoes: OpcoesDaChamada = {},
): Promise<void> {
  const resposta = await chamar(caminho, metodo, opcoes);
  // Ler o envelope, e não só o status: estas são as chamadas que CRIAM coisa por pedido da
  // pessoa (entrar, aceitar convite, redefinir senha), e a recusa delas vem com o motivo
  // escrito para quem digita. Descartá-lo aqui foi metade do defeito 3.5 — a página não
  // tinha como dizer por que a senha foi recusada, porque o porquê nunca chegava até ela.
  if (!resposta.ok) throw await erroDaResposta(resposta);
}

/* ------------------------------------------------------------------ saúde */

/* A validação é fechada de propósito: se a API responder algo fora do formato — inclusive
   um HTML de erro do nginx com status 200 — a tela mostra falha em vez de inventar um
   estado saudável. */
export const esquemaSaude = z.object({
  status: z.string().min(1),
  versao: z.string().min(1),
  ambiente: z.string().min(1),
  banco: z.enum(["ok", "erro"]),
  /* Com que custódia a chave mestra do cofre opera. Está no endereço PÚBLICO de propósito, e o
     custo foi aceito de olho aberto: contar ao mundo que a chave mestra pode estar num arquivo
     do host é menos ruim do que um cofre que parece seguro e não é. `provisoria` e
     `indisponivel` derrubam `status` para `degradado` — sem este campo aqui, o painel mostraria
     "API no ar, banco conectado" enquanto o `status` do servidor dizia `degradado`, e ninguém
     que lesse esta tela saberia por quê. */
  cofre: z.enum(["desligado", "provisoria", "gerenciada", "indisponivel"]),
});

export type Saude = z.infer<typeof esquemaSaude>;

export function buscarSaude(opcoes: { signal?: AbortSignal } = {}): Promise<Saude> {
  return pedirJson("/saude", esquemaSaude, opcoes);
}

/* --------------------------------------------------------------- identidade */

function primeiroPreenchido(valores: Array<string | undefined | null>): string | null {
  for (const valor of valores) {
    const limpo = valor?.trim();
    if (limpo) return limpo;
  }
  return null;
}

/* Organização e papel chegam da API ora como texto, ora como objeto — a frente de backend
   está sendo escrita em paralelo e o contrato acordado fixa só o nome do campo. Aceitar as
   duas formas evita que a tela de convite quebre por um detalhe de serialização; o que a
   pessoa lê é o mesmo rótulo nos dois casos. Vazio continua sendo erro de contrato. */
const esquemaRotulo = z
  .union([
    z.string(),
    z.object({
      nome: z.string().optional(),
      nome_fantasia: z.string().optional(),
      razao_social: z.string().optional(),
      chave: z.string().optional(),
    }),
  ])
  .transform((valor) =>
    typeof valor === "string"
      ? valor.trim()
      : (primeiroPreenchido([
          valor.nome,
          valor.nome_fantasia,
          valor.razao_social,
          valor.chave,
        ]) ?? ""),
  )
  .refine((texto) => texto.length > 0, "rótulo vazio");

const esquemaOrganizacao = z
  .object({
    id: z.string().optional(),
    nome: z.string().optional(),
    nome_fantasia: z.string().optional(),
    razao_social: z.string().optional(),
    cnpj: z.string().optional(),
  })
  .transform((organizacao) => ({
    id: organizacao.id ?? null,
    nome: primeiroPreenchido([
      organizacao.nome_fantasia,
      organizacao.razao_social,
      organizacao.nome,
    ]),
    cnpj: organizacao.cnpj ?? null,
  }));

const esquemaUsuario = z.object({
  id: z.string().min(1),
  email: z.string().min(1),
  nome: z.string().min(1),
  /* Marcador explícito de lado. Quando a API não o manda, o modelo de dados diz que
     usuário sem organização é interno — e é assim que `ehInterno` decide. */
  interno: z.boolean().optional(),
  papeis: z.array(esquemaRotulo).optional(),
});

export const esquemaSessao = z.object({
  usuario: esquemaUsuario,
  organizacao: esquemaOrganizacao.nullable(),
  // Ausente vira lista vazia: sessão sem permissão declarada não abre nada.
  permissoes: z.array(z.string()).default([]),
});

export type Sessao = z.infer<typeof esquemaSessao>;

/**
 * A sessão de quem está no navegador, ou `null` quando não há sessão.
 *
 * `401` é resposta esperada — é o que a API responde para quem ainda não entrou — e por
 * isso não vira erro. Qualquer outra falha vira exceção: não saber se há sessão nunca pode
 * ser confundido com saber que não há.
 */
export async function buscarSessao(
  opcoes: { signal?: AbortSignal } = {},
): Promise<Sessao | null> {
  const resposta = await chamar("/sessao/eu", "GET", opcoes);
  if (resposta.status === 401) return null;
  // A política de rede da organização recusa o endereço de quem pediu: a mensagem é escrita para
  // a pessoa ("acesse pela rede da empresa") e precisa chegar até a tela de entrada.
  if (resposta.status === 403) throw await erroDaResposta(resposta);
  exigirSucesso(resposta);
  return lerJson(resposta, esquemaSessao);
}

/** Falta o segundo fator: o login NÃO abriu sessão e devolveu este desafio. */
export const esquemaDesafioDeMfa = z.object({
  desafio: z.string().min(1),
  /** `verificar`: digite o código. `inscrever`: a política exige configurar o aplicativo agora. */
  etapa: z.enum(["verificar", "inscrever"]),
});

export type DesafioDeMfa = z.infer<typeof esquemaDesafioDeMfa>;

/**
 * Entrada por senha: `null` quando a sessão abriu (`204`), ou o desafio do segundo fator
 * (`202`). O `202` é o jeito de a API dizer "a senha conferiu, mas a entrada não terminou" — e
 * nenhum cookie é gravado até o segundo fator ser provado.
 */
async function entrarOuDesafiar(caminho: string, corpo: unknown): Promise<DesafioDeMfa | null> {
  const resposta = await chamar(caminho, "POST", { corpo });
  if (resposta.status === 202) return lerJson(resposta, esquemaDesafioDeMfa);
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return null;
}

export function abrirSessao(email: string, senha: string): Promise<DesafioDeMfa | null> {
  return entrarOuDesafiar("/sessao", { email, senha });
}

/** Encerra a sessão. Já estar sem sessão é o resultado desejado, não uma falha. */
export async function encerrarSessao(): Promise<void> {
  const resposta = await chamar("/sessao", "DELETE");
  if (resposta.status === 401) return;
  exigirSucesso(resposta);
}

/* ----------------------------------------------------------------- convites */

export const esquemaConvite = z.object({
  email: z.string().min(1),
  organizacao: esquemaRotulo,
  papel: esquemaRotulo,
});

export type Convite = z.infer<typeof esquemaConvite>;

/**
 * Dados do convite, ou `null` quando o token não vale mais.
 *
 * Token inválido, já usado e expirado são a mesma resposta de propósito: distinguir os
 * casos contaria a quem tem o link se ele existiu algum dia.
 */
export async function buscarConvite(
  token: string,
  opcoes: { signal?: AbortSignal } = {},
): Promise<Convite | null> {
  const resposta = await chamar(`/convites/${encodeURIComponent(token)}`, "GET", opcoes);
  if (resposta.status === 404 || resposta.status === 410) return null;
  exigirSucesso(resposta);
  return lerJson(resposta, esquemaConvite);
}

export function aceitarConvite(token: string, senha: string): Promise<DesafioDeMfa | null> {
  return entrarOuDesafiar(`/convites/${encodeURIComponent(token)}/aceitar`, { senha });
}

/* ------------------------------------------------------------- recuperação */

/**
 * Pede a recuperação de senha. A API responde `204` exista o e-mail ou não, e a tela
 * mostra a mesma frase nos dois casos — é o que impede a página de virar um verificador
 * de quem tem conta no portal.
 */
export function solicitarRecuperacao(email: string): Promise<void> {
  return pedirSemConteudo("/recuperacoes", "POST", { corpo: { email } });
}

export function redefinirSenha(token: string, senha: string): Promise<void> {
  return pedirSemConteudo(`/recuperacoes/${encodeURIComponent(token)}/redefinir`, "POST", {
    corpo: { senha },
  });
}
